// tp-e-comprecom-v1.js — custom element <tp-comprecom> ("Leve mais por menos").
//
// Custom element em vez de init no DOMContentLoaded porque a seção pode ser
// re-renderizada (theme editor, troca de variante do main-product) e o
// connectedCallback roda sozinho quando o elemento volta ao DOM.
//
// Responsabilidades:
//   - recalcular preço por card (variante x quantidade) e o total do combo;
//   - manter o botão coerente com o que está selecionado;
//   - adicionar todos os itens marcados em uma única chamada ao /cart/add.js.
//
// Toda a origem de preço é o dataset do markup (centavos, vindos do Liquid):
// nenhum valor é buscado por fetch, então a seção funciona offline do carrinho.

if (!customElements.get("tp-comprecom")) {
  class TpComprecom extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.cards = Array.from(this.querySelectorAll("[data-tp-cpc-card]"));
      this.button = this.querySelector("[data-tp-cpc-submit]");

      // Sem cards ou sem botão não há combo para montar. Sai quieto: um render
      // parcial (metafield vazio, produto despublicado) não deve estourar.
      if (!this.cards.length || !this.button) return;

      this.initialized = true;

      this.totalEl = this.querySelector("[data-tp-cpc-total]");
      this.totalCompareEl = this.querySelector("[data-tp-cpc-total-compare]");
      this.pixEl = this.querySelector("[data-tp-cpc-pix]");
      this.savingsEl = this.querySelector("[data-tp-cpc-savings]");

      this.currency = this.dataset.currency || "BRL";
      this.locale = this.dataset.locale || "pt-BR";
      this.pixDiscount = parseFloat(this.dataset.pixDiscount || 0) || 0;
      this.pixText = this.dataset.pixText || "";
      this.savingsTemplate = this.dataset.savingsTemplate || "";
      this.buttonText = this.button.textContent.trim();

      this.onChange = this.onChange.bind(this);
      this.addEventListener("change", this.onChange);
      this.button.addEventListener("click", (event) => {
        event.preventDefault();
        this.addToCart();
      });

      this.update();
    }

    disconnectedCallback() {
      this.removeEventListener("change", this.onChange);
      if (this.restoreTimer) clearTimeout(this.restoreTimer);
    }

    onChange(event) {
      if (!event.target.closest("[data-tp-cpc-card]")) return;
      this.update();
    }

    // Estado atual de um card: variante escolhida, quantidade e preços em centavos.
    readCard(card) {
      const checkbox = card.querySelector("[data-tp-cpc-select]");
      const variantSelect = card.querySelector("[data-tp-cpc-variant]");
      const quantitySelect = card.querySelector("[data-tp-cpc-quantity]");

      // Com seletor de variante os preços vêm da <option>; sem ele, do card.
      const source = variantSelect
        ? variantSelect.options[variantSelect.selectedIndex]
        : card;
      const data = source ? source.dataset : {};

      const price = parseInt(data.price, 10) || 0;
      const compare = parseInt(data.compare, 10) || 0;

      return {
        card,
        variantId: data.variantId || "",
        quantity: parseInt(quantitySelect ? quantitySelect.value : 1, 10) || 1,
        price,
        // compare_at_price abaixo do preço não é desconto: ignora.
        compare: compare > price ? compare : price,
        available: data.available === "true",
        selected: Boolean(checkbox && checkbox.checked && !checkbox.disabled),
      };
    }

    update() {
      let total = 0;
      let compareTotal = 0;
      let selectedCount = 0;

      this.cards.forEach((card) => {
        const state = this.readCard(card);

        card.classList.toggle("is-unselected", !state.selected);
        this.renderCardPrices(card, state);

        if (!state.selected || !state.available) return;

        total += state.price * state.quantity;
        compareTotal += state.compare * state.quantity;
        selectedCount += 1;
      });

      this.renderSummary(total, compareTotal, selectedCount);
    }

    // Preço do card = preço da linha (unitário x quantidade).
    renderCardPrices(card, state) {
      const priceEl = card.querySelector("[data-tp-cpc-price]");
      const compareEl = card.querySelector("[data-tp-cpc-compare]");

      if (priceEl) priceEl.textContent = this.formatMoney(state.price * state.quantity);

      if (compareEl) {
        const hasDiscount = state.compare > state.price;
        compareEl.hidden = !hasDiscount;
        if (hasDiscount) {
          compareEl.textContent = this.formatMoney(state.compare * state.quantity);
        }
      }
    }

    renderSummary(total, compareTotal, selectedCount) {
      if (this.totalEl) this.totalEl.textContent = this.formatMoney(total);

      if (this.totalCompareEl) {
        const hasDiscount = compareTotal > total;
        this.totalCompareEl.hidden = !hasDiscount;
        if (hasDiscount) this.totalCompareEl.textContent = this.formatMoney(compareTotal);
      }

      if (this.pixEl) {
        const pixTotal = total * ((100 - this.pixDiscount) / 100);
        this.pixEl.hidden = this.pixDiscount <= 0 || total <= 0;
        this.pixEl.textContent = `${this.formatMoney(pixTotal)} ${this.pixText}`.trim();
      }

      if (this.savingsEl) {
        const savings = compareTotal - total;
        this.savingsEl.hidden = savings <= 0 || !this.savingsTemplate;
        if (!this.savingsEl.hidden) {
          this.savingsEl.textContent = this.savingsTemplate.replace(
            "{valor}",
            this.formatMoney(savings),
          );
        }
      }

      this.button.disabled = selectedCount === 0;
    }

    formatMoney(cents) {
      const value = Math.round(cents) / 100;

      try {
        return new Intl.NumberFormat(this.locale, {
          style: "currency",
          currency: this.currency,
        }).format(value);
      } catch (error) {
        // Locale/moeda inválidos no admin não podem derrubar o cálculo.
        return `R$ ${value.toFixed(2).replace(".", ",")}`;
      }
    }

    async addToCart() {
      if (this.button.disabled) return;

      const items = this.cards
        .map((card) => this.readCard(card))
        .filter((state) => state.selected && state.available && state.variantId)
        .map((state) => ({ id: state.variantId, quantity: state.quantity }));

      if (!items.length) return;

      const addUrl = `${(window.routes && window.routes.cart_add_url) || "/cart/add"}.js`;

      try {
        this.button.disabled = true;
        this.button.textContent = "Adicionando...";

        const response = await fetch(addUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) throw new Error("Erro ao adicionar ao carrinho");

        this.button.textContent = "✓ Adicionado!";
        this.restoreButton(2000);
        this.openCartDrawer();
      } catch (error) {
        console.error("[tp-comprecom]", error);
        this.button.textContent = "Erro - tente novamente";
        this.restoreButton(2500);
      }
    }

    restoreButton(delay) {
      if (this.restoreTimer) clearTimeout(this.restoreTimer);
      this.restoreTimer = setTimeout(() => {
        this.button.textContent = this.buttonText;
        this.update();
      }, delay);
    }

    async openCartDrawer() {
      const drawer = document.querySelector("cart-drawer");

      // Sem drawer (tema com página de carrinho) o cliente vai para /cart.
      if (!drawer || typeof drawer.renderContents !== "function") {
        window.location.href = (window.routes && window.routes.cart_url) || "/cart";
        return;
      }

      const response = await fetch("/?sections=cart-drawer,cart-icon-bubble");
      const sections = await response.json();

      drawer.classList.remove("is-empty");
      drawer.renderContents({ sections });

      requestAnimationFrame(() => {
        drawer.classList.add("animate", "active");
        document.body.classList.add("overflow-hidden");
      });
    }
  }

  customElements.define("tp-comprecom", TpComprecom);
}
